import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, MapPin, Users, Car, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { VEHICLE_TYPES } from "@/data/site";
import { createBooking } from "@/lib/api";

const inputCls = "bg-surface border-transparent rounded-xl h-12 px-4 text-ink placeholder:text-ink/35 focus:bg-white focus:border-brand focus-visible:ring-2 focus-visible:ring-brand/20 transition-colors";
const labelCls = "text-[11px] font-bold text-ink/50 uppercase tracking-[0.12em] mb-2 flex items-center gap-1.5";

const empty = {
  name: "", phone: "", email: "", pickup: "", dropoff: "",
  time: "", passengers: "1", vehicle_type: "Saloon", notes: "",
};

export const BookingForm = ({ compact = false, defaultArea = "" }) => {
  const [form, setForm] = useState({ ...empty, pickup: defaultArea ? `${defaultArea}` : "" });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pickup || !form.dropoff || !date || !form.time) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await createBooking({
        ...form,
        passengers: parseInt(form.passengers, 10) || 1,
        date: format(date, "yyyy-MM-dd"),
        area: defaultArea || undefined,
      });
      setDone(true);
      toast.success("Booking request received! We'll call to confirm shortly.");
      setForm({ ...empty });
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="bg-white rounded-2xl p-10 text-center shadow-[0_24px_50px_-12px_rgba(0,0,0,0.15)]">
        <div className="mx-auto mb-5 grid place-items-center h-16 w-16 rounded-full bg-brand/10 text-brand">
          <CheckCircle2 className="h-9 w-9" strokeWidth={2} />
        </div>
        <h3 className="font-heading text-2xl font-bold text-ink mb-3">Request Received</h3>
        <p className="text-ink/60 mb-6">
          Thank you. One of our team will call you shortly to confirm your journey and fixed fare.
        </p>
        <Button data-testid="booking-another-btn" onClick={() => setDone(false)} className="bg-brand hover:bg-brand-light text-white rounded-full px-8 font-bold">
          Make Another Booking
        </Button>
      </div>
    );
  }

  return (
    <form
      data-testid="booking-form"
      onSubmit={submit}
      className="bg-white rounded-2xl p-6 md:p-7 shadow-[0_24px_50px_-12px_rgba(0,0,0,0.15)]"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="h-2 w-2 rounded-full bg-brand animate-pulse" />
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-brand">Instant callback</span>
      </div>
      <h3 className="font-heading text-2xl font-bold text-ink mb-5">Book your journey</h3>

      <div className={cn("grid gap-3.5", compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2")}>
        <div>
          <Label className={labelCls}><MapPin className="h-3.5 w-3.5 text-brand" /> Pick-up *</Label>
          <Input data-testid="booking-pickup" className={inputCls} placeholder="Pick-up address" value={form.pickup} onChange={set("pickup")} />
        </div>
        <div>
          <Label className={labelCls}><MapPin className="h-3.5 w-3.5 text-brand" /> Drop-off *</Label>
          <Input data-testid="booking-dropoff" className={inputCls} placeholder="Destination" value={form.dropoff} onChange={set("dropoff")} />
        </div>

        <div>
          <Label className={labelCls}><CalendarIcon className="h-3.5 w-3.5 text-brand" /> Date *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                data-testid="booking-date-trigger"
                variant="outline"
                className={cn("w-full h-12 rounded-xl justify-start bg-surface border-transparent hover:bg-surface hover:border-brand text-left font-normal px-4", !date && "text-ink/35")}
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-brand" />
                {date ? format(date, "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white border-[#EAEBEA] rounded-2xl" align="start">
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className={labelCls}><Clock className="h-3.5 w-3.5 text-brand" /> Time *</Label>
          <Input data-testid="booking-time" type="time" className={inputCls} value={form.time} onChange={set("time")} />
        </div>

        <div>
          <Label className={labelCls}><Users className="h-3.5 w-3.5 text-brand" /> Passengers</Label>
          <Select value={form.passengers} onValueChange={set("passengers")}>
            <SelectTrigger data-testid="booking-passengers" className={inputCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-[#EAEBEA] text-ink rounded-xl">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <SelectItem key={n} value={String(n)}>{n} passenger{n > 1 ? "s" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={labelCls}><Car className="h-3.5 w-3.5 text-brand" /> Vehicle</Label>
          <Select value={form.vehicle_type} onValueChange={set("vehicle_type")}>
            <SelectTrigger data-testid="booking-vehicle" className={inputCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-[#EAEBEA] text-ink rounded-xl">
              {VEHICLE_TYPES.map((v) => (
                <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className={labelCls}>Your Name *</Label>
          <Input data-testid="booking-name" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
        </div>
        <div>
          <Label className={labelCls}>Phone *</Label>
          <Input data-testid="booking-phone" className={inputCls} placeholder="Contact number" value={form.phone} onChange={set("phone")} />
        </div>

        <div className="md:col-span-2">
          <Label className={labelCls}>Notes (optional)</Label>
          <Textarea data-testid="booking-notes" className="bg-surface border-transparent rounded-xl px-4 text-ink placeholder:text-ink/35 focus:bg-white focus:border-brand focus-visible:ring-2 focus-visible:ring-brand/20" placeholder="Flight number, child seat, luggage etc." value={form.notes} onChange={set("notes")} />
        </div>
      </div>

      <Button
        type="submit"
        data-testid="booking-submit"
        disabled={loading}
        className="mt-5 w-full py-4 h-auto bg-brand hover:bg-brand-light text-white rounded-full text-base font-bold transition-all duration-300 hover:shadow-[0_8px_24px_rgba(31,157,42,0.35)] active:scale-[0.98]"
      >
        {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…</> : "Get my fixed fare"}
      </Button>
      <p className="mt-3 text-center text-xs text-ink/40">No payment now · Free cancellation · Fixed price</p>
    </form>
  );
};
